/**
 * Stock Price Chart Types
 * Shared types for the multi-timeframe stock price chart component
 */

export type TimeRange = '1D' | '1W' | '1M' | '3M' | '6M' | 'YTD' | '1Y' | '5Y' | '10Y';

export type DataResolution = 'intraday-1min' | 'intraday-15min' | 'intraday-30min' | 'intraday-1hour' | 'intraday-4hour' | 'daily' | 'dynamic';

export interface TimeRangeConfig {
  label: string;
  resolution: DataResolution;
  xAxisFormat: string;
  metricsLabel: string;
}

export const TIME_RANGE_CONFIG: Record<TimeRange, TimeRangeConfig> = {
  '1D': {
    label: '1D',
    resolution: 'intraday-1min',
    xAxisFormat: 'HH:mm',
    metricsLabel: 'Day Range',
  },
  '1W': {
    label: '1W',
    resolution: 'intraday-15min',
    xAxisFormat: 'ddd HH:mm',
    metricsLabel: 'Week Range',
  },
  '1M': {
    label: '1M',
    resolution: 'intraday-30min',
    xAxisFormat: 'MMM D HH:mm',
    metricsLabel: 'Month Range',
  },
  '3M': {
    label: '3M',
    resolution: 'intraday-4hour',
    xAxisFormat: 'MMM D HH:mm',
    metricsLabel: '3M Range',
  },
  '6M': {
    label: '6M',
    resolution: 'intraday-4hour',
    xAxisFormat: 'MMM D HH:mm',
    metricsLabel: '6M Range',
  },
  'YTD': {
    label: 'YTD',
    resolution: 'dynamic', // Actual resolution determined at runtime based on days since Jan 1
    xAxisFormat: 'dynamic', // Actual format determined at runtime
    metricsLabel: 'YTD Range',
  },
  '1Y': {
    label: '1Y',
    resolution: 'daily',
    xAxisFormat: 'YYYY-MM-DD',
    metricsLabel: '52W Range',
  },
  '5Y': {
    label: '5Y',
    resolution: 'daily',
    xAxisFormat: 'YYYY-MM-DD',
    metricsLabel: '5Y Range',
  },
  '10Y': {
    label: '10Y',
    resolution: 'daily',
    xAxisFormat: 'YYYY-MM-DD',
    metricsLabel: '10Y Range',
  },
};

// The ticker page's ranges. 3M is offered only where a chart asks for it
// (the screener's open row).
export const ALL_TIME_RANGES: TimeRange[] = ['1D', '1W', '1M', '6M', 'YTD', '1Y', '5Y', '10Y'];

/**
 * Raw price record from any API endpoint.
 * Each hook normalizes its response to this shape.
 */
export interface RawPriceRecord {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

/**
 * Result from each individual time range query.
 * Provides independent loading/error states.
 */
export interface TimeRangeQueryResult {
  data: RawPriceRecord[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  /** Metadata from API response */
  metadata?: {
    fallbackUsed?: boolean;
    actualDate?: string;
    actualFrom?: string;
    recordCount?: number;
  };
}

/**
 * Combined result from useStockPriceData hook.
 * Maps each time range to its query result.
 */
export type StockPriceDataResult = Record<TimeRange, TimeRangeQueryResult>;
